import { Link, useNavigate } from 'react-router-dom';
import { Button, Card, EmptyState, Spinner } from './index.js';
import { useMarkRead, useNotifications } from '../hooks/useNotifications.js';

const ICONS = {
  low_stock: '📦',
  new_order: '🧾',
  new_subscription: '🔁',
  payment_received: '💳',
  partner_application: '🪪',
  dispatch_unfilled: '🚨',
  delivery_failed: '⚠️',
  delivery_late: '⏰',
  delivery_nudge: '⏰',
  delivery_stalled: '🛑',
  driver_arrived: '📍',
  driver_assignment: '🛵',
  delivery_offer: '📍',
  driver_reminder: '🗓️',
  driver_review: '✅',
  announcement: '📣',
  menu_broadcast: '🍉',
  payout_requested: '💸',
  new_review: '⭐',
  payout_approved: '👍',
  payout_paid: '💸',
  payout_rejected: '↩️',
};

// Alerts that need someone to act get a warm tint so they stand out.
const URGENT = new Set(['low_stock', 'dispatch_unfilled', 'delivery_failed', 'delivery_late', 'delivery_stalled']);

function timeAgo(iso) {
  const mins = Math.round((Date.now() - new Date(iso).getTime()) / 60000);
  if (mins < 1) return 'just now';
  if (mins < 60) return `${mins} min ago`;
  const hrs = Math.round(mins / 60);
  if (hrs < 24) return `${hrs} h ago`;
  return new Date(iso).toLocaleDateString('en-IN', { day: 'numeric', month: 'short' });
}

function dayBucket(iso) {
  const d = new Date(iso);
  const today = new Date();
  const y = new Date(); y.setDate(today.getDate() - 1);
  if (d.toDateString() === today.toDateString()) return 'Today';
  if (d.toDateString() === y.toDateString()) return 'Yesterday';
  return 'Earlier';
}

/**
 * The notification inbox, shared by the admin and driver apps. Tapping an item
 * marks it read and opens where it points.
 */
export default function NotificationsInbox({ padded = true, backTo = null }) {
  const navigate = useNavigate();
  const { data, isLoading, isError, refetch } = useNotifications();
  const markRead = useMarkRead();
  const items = data?.notifications ?? [];
  const unread = data?.unread ?? 0;

  const open = (n) => {
    if (!n.readAt) markRead.mutate([n.id]);
    if (n.link) navigate(n.link);
  };

  const groups = ['Today', 'Yesterday', 'Earlier']
    .map((label) => ({ label, items: items.filter((n) => dayBucket(n.createdAt) === label) }))
    .filter((g) => g.items.length);

  return (
    <div className={padded ? 'mx-auto max-w-2xl space-y-4 p-4 sm:p-6' : 'space-y-4'}>
      {backTo && (
        <Link to={backTo} className="inline-flex min-h-touch items-center text-sm font-semibold text-brand">
          ‹ Back
        </Link>
      )}
      <div className="flex flex-wrap items-end justify-between gap-3">
        <div>
          <span className="eyebrow">Inbox</span>
          <h1 className="mt-1 font-display text-2xl text-heading sm:text-3xl">Notifications</h1>
          <p className="mt-1 text-sm text-ink-muted">
            {unread ? `${unread} unread` : 'You’re all caught up.'}
          </p>
        </div>
        {unread > 0 && (
          <Button size="sm" variant="secondary" loading={markRead.isPending} onClick={() => markRead.mutate()}>
            Mark all read
          </Button>
        )}
      </div>

      {isLoading ? (
        <div className="flex justify-center py-16"><Spinner label="Loading notifications" /></div>
      ) : isError ? (
        <Card tone="quiet">
          <p className="text-sm text-ink">We couldn’t load your notifications.</p>
          <Button className="mt-3" size="sm" onClick={() => refetch()}>Try again</Button>
        </Card>
      ) : items.length === 0 ? (
        <EmptyState icon="🔔" title="No notifications yet" message="Orders, alerts and updates will show up here." />
      ) : (
        groups.map((g) => (
          <section key={g.label}>
            <h2 className="eyebrow mb-2">{g.label}</h2>
            <ul className="overflow-hidden rounded-crate border border-oat-dark bg-surface shadow-soft">
              {g.items.map((n) => {
                const urgent = URGENT.has(n.type);
                return (
                  <li key={n.id} className="border-b border-oat-dark/60 last:border-0">
                    <button
                      type="button"
                      onClick={() => open(n)}
                      className={`flex w-full items-start gap-3 px-4 py-3.5 text-left transition-colors active:bg-leaf-50 sm:hover:bg-leaf-50 ${
                        !n.readAt && urgent ? 'bg-persimmon-light/60' : !n.readAt ? 'bg-leaf-50/70' : ''
                      }`}
                    >
                      <span aria-hidden="true" className={`grid h-10 w-10 shrink-0 place-items-center rounded-full text-lg ${urgent ? 'bg-persimmon-light' : 'bg-leaf-100'}`}>
                        {ICONS[n.type] ?? '🔔'}
                      </span>
                      <span className="min-w-0 flex-1">
                        <span className="flex items-start justify-between gap-2">
                          <span className={`text-sm leading-snug text-heading ${n.readAt ? 'font-medium' : 'font-bold'}`}>
                            {n.title}
                          </span>
                          <span className="shrink-0 text-2xs text-ink-faint">{timeAgo(n.createdAt)}</span>
                        </span>
                        {n.body && <span className="mt-0.5 block text-sm leading-relaxed text-ink-muted">{n.body}</span>}
                      </span>
                      {!n.readAt && <span className="mt-1.5 h-2.5 w-2.5 shrink-0 rounded-full bg-persimmon" aria-label="Unread" />}
                    </button>
                  </li>
                );
              })}
            </ul>
          </section>
        ))
      )}
    </div>
  );
}
